import { getOrCreateMainLogger } from "../logger/main";

import { Manifest, MainProcessBusEvent } from "../types/types";
import { BrowserWindow } from "electron";
import sizeOfImage from "image-size";

const logger = getOrCreateMainLogger();

type SplashScreenOptions = {
	splashScreenImage: string;
	options?: any;
};

type SplashScreenDimensions = {
	width: number;
	height: number;
};
export class SplashScreen {
	splashScreenWindow: Electron.BrowserWindow | null = null;

	constructor() {
		this.hideSplashScreenFromBus = this.hideSplashScreenFromBus.bind(this);
		this.showSplashScreenFromBus = this.showSplashScreenFromBus.bind(this);
	}

	/**
	 * Show the splash screen from a bus event
	 */
	async showSplashScreenFromBus(eventObj: MainProcessBusEvent): Promise<void> {
		const { imageURL } = eventObj.data;
		try {
			await this.showSplashScreen({ splashScreenImage: imageURL });
			eventObj.respond({
				success: true,
			});
		} catch (e: any) {
			logger.error(`Show splash screen from bus error: ${e.message}`);
			eventObj.respond({
				error: e.message,
			});
		}
	}

	/**
	 * Closes the current splash screen if it is not already closed.
	 */
	async hideSplashScreen(): Promise<void> {
		try {
			logger.log("APPLICATION LIFECYCLE: Closing splash screen");
			if (!this.splashScreenWindow) return;
			this.splashScreenWindow.close();
			this.splashScreenWindow = null;
		} catch (e: any) {
			logger.error(`Unable to hide splash screen ${e.message}`);
			throw new Error("Error hiding splash screen.");
		}
	}

	/**
	 * Closes the current splash screen from a bus event.
	 */
	async hideSplashScreenFromBus(eventObj: MainProcessBusEvent): Promise<void> {
		if (!this.splashScreenWindow) {
			eventObj.respond({ success: true });
			return;
		}
		try {
			this.hideSplashScreen();
			eventObj.respond({ success: true });
		} catch (e: any) {
			eventObj.respond({ error: e.message });
		}
	}

	/**
	 * Given a URL that points to an image file type, it tries to calculate its
	 * dimensions.
	 *
	 * @param imageURL The image URL
	 * @returns The dimensions (width and height) of the image
	 */
	async getImageSize(imageURL: string) {
		const img = await fetch(imageURL);
		const buffer = Buffer.from(await img.arrayBuffer());
		return sizeOfImage(buffer);
	}

	/**
	 * Show a splash screen using the provided imageURL for the specified timeout or until hideSplashScreen is called
	 */
	async showSplashScreen(splashScreenOptions: SplashScreenOptions): Promise<void> {
		const { splashScreenImage, options } = splashScreenOptions;
		if (typeof splashScreenImage !== "string") {
			throw new Error(`provided URL for splash screen must be a string: ${splashScreenImage}`);
		}

		try {
			// check to see if URL is valid
			new URL(splashScreenImage);
		} catch (e) {
			throw new Error(`provided url for splash screen is not valid: ${splashScreenImage}`);
		}

		try {
			// We use image-size library because Electron's nativeImage.getSize() cannot process some file types
			// we start with default dimensions from Electron.BrowserWindowConstructorOptions
			let splashScreenDimensions: SplashScreenDimensions | any = {
				height: options?.height ?? 400,
				width: options?.width ?? 600,
			};

			try {
				// we only want to check sizes for images, not HTML pages
				if (!splashScreenImage.toLowerCase().endsWith(".html")) {
					splashScreenDimensions = await this.getImageSize(splashScreenImage);
					logger.log(
						`Displaying splash screen ${splashScreenImage} with height: ${splashScreenDimensions.height} and width ${splashScreenDimensions.width}`
					);
				}
			} catch (ex) {
				logger.error(
					`Could not determine image size for splash screen ${splashScreenImage}.  
				Reverting to default dimensions of width: ${splashScreenDimensions.width}px height: ${splashScreenDimensions.height}px which may lead to off center or cropping when displayed.`,
					ex
				);
			}

			// When the toolbar claims space, the OS will scale windows appropriately, which causes overflow.
			// Making the window larger than the image will cause it to be off-center
			// Handling the moved event will cause a flicker of overflow
			// Let's instead just pad it.  With a 30 pixel toolbar and a minimum of 600 vertical lines,
			// a 5% increase should suffice.
			const fudgeFactor = splashScreenImage.toLowerCase().includes(".svg") ? 1.05 : 1.0;

			// delete the width and height to avoid overriding the values below
			if (options) {
				delete options.width;
				delete options.height;
			}
			this.splashScreenWindow = new BrowserWindow({
				width: Math.floor(splashScreenDimensions.width * fudgeFactor),
				height: Math.floor(splashScreenDimensions.height * fudgeFactor),
				frame: false,
				resizable: false,
				transparent: true,
				alwaysOnTop: true,
				skipTaskbar: true,
				backgroundColor: "#00FFFFFF",
				...options,
			});

			this.splashScreenWindow.loadURL(splashScreenImage);
		} catch (e: any) {
			logger.error(`Unable to load splash screen ${e.message}`);
			throw new Error("Error loading splash screen.");
		}
	}

	maybeShowSplashScreen(manifest: Manifest) {
		const { splashScreenImage, splashScreenOptions } = manifest;

		const splashScreenOpts: SplashScreenOptions = { splashScreenImage, options: splashScreenOptions };
		if (splashScreenImage) this.showSplashScreen(splashScreenOpts);
	}
}
